// /pricing — public Coach Pro pricing page (STRIPE_SPEC Phase 2).
//
// Free vs Pro comparison with monthly/annual toggle. CTA logic by session
// state:
//   - anon → "Sign in to upgrade" → sign-in flow, return to /pricing
//   - agent → no CTA, short note: "HoopRoster is free for agents."
//   - coach, tier=free → Upgrade buttons → POST /api/stripe/checkout with
//     {interval:'monthly'|'annual'} → redirect; graceful error on failure
//   - coach, tier=pro → "You're on Coach Pro" + link to dashboard billing
//
// Feature rows per STRIPE_SPEC §2:
//   Free: browse, filters, public stats, Compare, Update stats 5/day
//   Pro: uncapped Update stats, saved searches and contactable references
//   (all live, Pro-gated in billing.ts) + "coming" rows (email alerts,
//   direct messaging). Corrected 2026-10-18: saved searches and contactable
//   references had carried "(coming)" since Phase 2 although both shipped
//   in July — the public page understated what Pro already includes.

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import { useSession, type AppRole } from '../lib/auth'
import { apiJson } from '../lib/api'
import { useMeta } from '../hooks/useMeta'

type Interval = 'monthly' | 'annual'
type Tier = 'free' | 'pro'

interface MeResponse {
  profile: {
    role: AppRole | null
    tier: Tier
  }
}

// STRIPE_SPEC §2 — feature rows. Free column lists the 5-item limit,
// Pro column shows what gates are lifted; only genuinely unbuilt items carry
// "(coming)". The spec says Compare stays free — so it's in the Free column,
// not Pro-gated.
const features = [
  { free: 'Browse players', pro: 'Browse players' },
  { free: 'Advanced filters', pro: 'Advanced filters' },
  { free: 'Public stats & profiles', pro: 'Public stats & profiles' },
  { free: 'Compare tool (unlimited)', pro: 'Compare tool (unlimited)' },
  { free: 'Update stats (5/day)', pro: 'Update stats (uncapped)' },
  { free: null, pro: 'Saved searches' },
  { free: null, pro: 'Email alerts (coming)' },
  { free: null, pro: 'Contactable references' },
  { free: null, pro: 'Direct messaging (coming)' },
]

export default function Pricing() {
  const canonical = window.location.origin + window.location.pathname
  useMeta({
    title: 'Pricing — HoopRoster',
    description:
      'Coach Pro pricing for HoopRoster. Browse players for free or upgrade for uncapped stats updates, saved searches, and contactable references.',
    canonical,
  })

  const { data: session, isPending: sessionPending } = useSession()
  const user = session?.user ?? null
  const [interval, setInterval] = useState<Interval>('monthly')
  const [role, setRole] = useState<AppRole | null>(null)
  const [tier, setTier] = useState<Tier>('free')
  const [meResolved, setMeResolved] = useState(false)
  const [upgrading, setUpgrading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      setRole(null)
      setTier('free')
      setMeResolved(true)
      return
    }
    setMeResolved(false)
    let cancelled = false
    apiJson<MeResponse>('/api/me')
      .then(({ profile }) => {
        if (cancelled) return
        setRole(profile.role)
        setTier(profile.tier)
        setMeResolved(true)
      })
      .catch(() => {
        if (cancelled) return
        setRole(null)
        setTier('free')
        setMeResolved(true)
      })
    return () => {
      cancelled = true
    }
  }, [user])

  async function handleUpgrade(targetInterval: Interval) {
    setUpgrading(true)
    setErr(null)
    try {
      const res = await apiJson<{ url?: string }>('/api/stripe/checkout', {
        method: 'POST',
        body: JSON.stringify({ interval: targetInterval }),
      })
      if (res.url) {
        window.location.href = res.url
      } else {
        setErr('checkout_no_url')
        setUpgrading(false)
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
      setUpgrading(false)
    }
  }

  const monthlyPrice = '$25'
  const annualPrice = '$250'
  const annualNote = '2 months free'

  const showUpgrade = user && role === 'coach' && tier === 'free' && meResolved
  const showCurrentPlan = user && role === 'coach' && tier === 'pro' && meResolved
  const showAgentNote = user && role === 'agent' && meResolved
  const showSignIn = !user && !sessionPending

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <header className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-hoop-text">
          Pricing
        </h1>
        <p className="text-sm text-hoop-text-3 mt-2 max-w-2xl mx-auto">
          HoopRoster is free for agents. Coaches get powerful browse tools for
          free; upgrade to Coach Pro for uncapped stats updates and advanced
          features as they ship.
        </p>
      </header>

      {/* Monthly/Annual toggle */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <button
          type="button"
          onClick={() => setInterval('monthly')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            interval === 'monthly'
              ? 'bg-hoop-primary text-hoop-primary-fg'
              : 'bg-hoop-surface text-hoop-text-2 hover:text-hoop-text border border-hoop-border'
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setInterval('annual')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            interval === 'annual'
              ? 'bg-hoop-primary text-hoop-primary-fg'
              : 'bg-hoop-surface text-hoop-text-2 hover:text-hoop-text border border-hoop-border'
          }`}
        >
          Annual
        </button>
      </div>

      {/* Error banner */}
      {err && (
        <div className="max-w-2xl mx-auto mb-6 border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm">
          {err === 'stripe_not_configured'
            ? 'Stripe is not configured yet. Please try again later.'
            : err === 'stripe_api_error'
              ? 'Unable to create checkout session. Please try again.'
              : err}
        </div>
      )}

      {/* Agent note */}
      {showAgentNote && (
        <div className="max-w-2xl mx-auto mb-6 border border-hoop-border bg-hoop-surface rounded-md px-4 py-3 text-sm text-hoop-text-2 text-center">
          HoopRoster is free for agents. No subscription needed.
        </div>
      )}

      {/* Pricing columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Free column */}
        <div className="border border-hoop-border rounded-lg p-6 bg-hoop-surface">
          <h2 className="text-xl font-bold text-hoop-text mb-2">Free</h2>
          <div className="text-3xl font-bold text-hoop-text mb-1">$0</div>
          <div className="text-sm text-hoop-text-3 mb-6">Forever</div>
          <ul className="space-y-3 mb-6">
            {features.map((f, i) =>
              f.free ? (
                <li key={i} className="flex items-start gap-2 text-sm text-hoop-text-2">
                  <Check className="w-4 h-4 text-hoop-green mt-0.5 flex-shrink-0" />
                  <span>{f.free}</span>
                </li>
              ) : (
                <li key={i} className="flex items-start gap-2 text-sm text-hoop-text-3">
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>—</span>
                </li>
              ),
            )}
          </ul>
          {showSignIn && (
            <Link
              to="/"
              className="block w-full text-center bg-hoop-surface border border-hoop-border text-hoop-text px-4 py-2 rounded-md text-sm font-medium hover:border-hoop-accent transition-colors"
            >
              Sign in to upgrade
            </Link>
          )}
        </div>

        {/* Pro column */}
        <div className="border-2 border-hoop-accent rounded-lg p-6 bg-hoop-surface relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-hoop-accent text-white text-xs font-bold px-3 py-1 rounded-full">
            RECOMMENDED
          </div>
          <h2 className="text-xl font-bold text-hoop-text mb-2">Coach Pro</h2>
          <div className="text-3xl font-bold text-hoop-text mb-1">
            {interval === 'monthly' ? monthlyPrice : annualPrice}
          </div>
          <div className="text-sm text-hoop-text-3 mb-6">
            {interval === 'monthly' ? 'per month' : `per year · ${annualNote}`}
          </div>
          <ul className="space-y-3 mb-6">
            {features.map((f, i) =>
              f.pro ? (
                <li key={i} className="flex items-start gap-2 text-sm text-hoop-text-2">
                  <Check className="w-4 h-4 text-hoop-green mt-0.5 flex-shrink-0" />
                  <span>{f.pro}</span>
                </li>
              ) : null,
            )}
          </ul>
          {showUpgrade && (
            <button
              type="button"
              disabled={upgrading}
              onClick={() => handleUpgrade(interval)}
              className="w-full bg-hoop-primary text-hoop-primary-fg px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40"
            >
              {upgrading ? 'Redirecting…' : 'Upgrade to Coach Pro'}
            </button>
          )}
          {showCurrentPlan && (
            <div className="text-center">
              <div className="text-sm text-hoop-green font-medium mb-2">
                ✓ You're on Coach Pro
              </div>
              <Link
                to="/dashboard"
                className="text-sm text-hoop-text-2 hover:text-hoop-text underline"
              >
                Manage subscription →
              </Link>
            </div>
          )}
          {showSignIn && (
            <Link
              to="/"
              className="block w-full text-center bg-hoop-primary text-hoop-primary-fg px-4 py-2 rounded-md text-sm font-medium hover:opacity-90"
            >
              Sign in to upgrade
            </Link>
          )}
        </div>
      </div>
    </main>
  )
}
